import { useContext, useEffect, useMemo, useState } from 'react';
import { Loader, Page, PageTitleWithButton } from '../../../../components';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { deepEqual } from 'fast-equals';
import { Box, Button, ButtonGroup, Divider, FormControlLabel, Grid, Switch } from '@mui/material';
import { Save, Close, ManageAccounts, Restore, EmailOutlined } from '@mui/icons-material';
import { ContactInfoTab } from './contact-info-tab';
import {
  createClient,
  getClientDetail,
  updateClient,
  resetClientPassword,
  sendClientWelcomeEmail
} from '../../../../fetch';
import { IClientContact, IClientDetail, UserRoles } from '../../../../models';
import { AdditionalContactsTab } from './additional-contacts-tab';
import { yupPhoneValidation } from '../../../../helpers';
import { LoadingButton } from '@mui/lab';
import { UserContext } from '../../../../context';
import { BuildsTab } from './builds-tab';
import { AddEditCustomErrorDisplay } from './add-edit-client-custom-validation';
import { AddEditClientTabs } from './addEditClientTabs';

export const enum clientDetailTabs {
  contactInformation,
  additionalContacts,
  builds
}

export const enum clientValidation {
  contactInformation = 'contactInformation',
  additionalContacts = 'additionalContacts'
}

export const clientDetailSchema = Yup.object().shape({
  personId: Yup.string().nullable(),
  isActive: Yup.boolean().nullable(),
  contactInformation: Yup.object().shape({
    agentId: Yup.string().required('Required'),
    firstName: Yup.string().required('Required').max(255, 'Max 255 characters'),
    lastName: Yup.string().required('Required').max(255, 'Max 255 characters'),
    contractName: Yup.string().required('Required').max(255, 'Max 255 characters'),
    address: Yup.string().max(255, 'Max 255 characters').required('Required'),
    address2: Yup.string().max(255, 'Max 255 characters').nullable(),
    city: Yup.string().max(255, 'Max 255 characters').required('Required'),
    state: Yup.string().required('Required'),
    zip: Yup.string().max(255, 'Max 255 characters').required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    phoneNumber: yupPhoneValidation().required('Required'),
    buyerHasAgent: Yup.boolean(),
    agentFirstName: Yup.string().when('buyerHasAgent', {
      is: val => val === true,
      then: Yup.string().required('Required').max(255, 'Max 255 characters'),
      otherwise: Yup.string().max(255, 'Max 255 characters').nullable()
    }),
    agentLastName: Yup.string().when('buyerHasAgent', {
      is: val => val === true,
      then: Yup.string().required('Required').max(255, 'Max 255 characters'),
      otherwise: Yup.string().max(255, 'Max 255 characters').nullable()
    }),
    agentEmail: Yup.string().when('buyerHasAgent', {
      is: val => val === true,
      then: Yup.string().email('Invalid email').required('Required'),
      otherwise: Yup.string().email('Invalid email').nullable()
    }),
    agentPhone: Yup.string().when('buyerHasAgent', {
      is: val => val === true,
      then: yupPhoneValidation(),
      otherwise: Yup.string().nullable()
    }),
    agentSellingCompany: Yup.string().when('buyerHasAgent', {
      is: val => val === true,
      then: Yup.string().max(255, 'Max 255 characters'),
      otherwise: Yup.string().max(255, 'Max 255 characters').nullable()
    })
  }),
  additionalContacts: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Required').max(255, 'Max 255 characters'),
        phoneNumber: yupPhoneValidation().required('Required'),
        email: Yup.string().email('Invalid email').required('Required')
      })
    )
    .nullable()
});

const rolesToCheck = [
  UserRoles.BLAdmin,
  UserRoles.BLSalesAdmin,
  UserRoles.BLAgent,
  UserRoles.BLSales
];

export const AddEditClient = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { user, isCloser } = useContext(UserContext);
  const { personId }: { personId: string } = useParams();

  const canResetPassword = user?.userRoles?.some(role => rolesToCheck.includes(role as UserRoles));
  const [isResettingUser, setIsResettingUser] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<clientDetailTabs>(
    clientDetailTabs.contactInformation
  );

  const getPersonId = () => {
    return personId ? personId : 'add';
  };

  const [isLoadingClientDetail, setIsLoadingClientDetail] = useState(false);
  const [clientDetail, setClientDetail] = useState<IClientDetail | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);

  const title = isLoadingClientDetail
    ? 'Loading...'
    : !clientName && personId === 'add'
    ? 'Add Purchaser'
    : clientName || 'Edit Purchaser';

  // NOTE: As we develop each tab, add validation to the link array below, so that the tab validation behaves as expected
  // The validation string should match the name of the Yup shape
  const clientDetailTabLinks = useMemo(
    () =>
      [
        {
          name: 'Contact Info',
          url: `/sales/contracting/purchasers/${getPersonId()}/contact-info`,
          value: clientDetailTabs.contactInformation,
          validation: clientValidation.contactInformation // name of yup shape
        },
        {
          name: 'Additional Contacts',
          url: `/sales/contracting/purchasers/${getPersonId()}/additional-contacts`,
          value: clientDetailTabs.additionalContacts,
          validation: clientValidation.additionalContacts
        },
        personId !== 'add' && {
          name: 'Builds',
          url: `/sales/contracting/purchasers/${getPersonId()}/builds`,
          value: clientDetailTabs.builds
        }
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [personId]
  );

  // hide this clients builds on under contract page? set value using switch - default is false //
  const [isBuildHidden, setIsBuildHidden] = useState<boolean>(false);
  const [isPurchaserHidden, setIsPurchaserHidden] = useState<boolean>(false);
  const isSalesAdmin =
    user?.userRoles?.includes('BL_SalesAdmin') || user?.userRoles?.includes('BL_Admin');

  const fetchClientDetail = async () => {
    if (personId !== 'add') {
      setIsLoadingClientDetail(true);
      try {
        const res = await getClientDetail(personId);
        setClientDetail(res);
        setIsBuildHidden(res.hideBuilds ?? false);
        setIsPurchaserHidden(res.hidePurchaser ?? false);
        setClientName(`${res.contactInformation.firstName} ${res.contactInformation.lastName}`);
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? `Error loading purchaser detail, please try again.`, {
          variant: 'error'
        });
      } finally {
        setIsLoadingClientDetail(false);
      }
    }
  };

  const navigateToManageUsers = () => {
    window.open(`/admin/user-tools/users?personId=${clientDetail?.personId}&tab=1`);
  };

  const navigateToManageUsersAdditionalContact = (contact: IClientContact) => {
    window.open(`/admin/user-tools/users?personId=${contact?.additionalUserId}&tab=1`);
  };

  function linkPerson() {
    if (user?.userRoles?.includes('BL_Admin') || user?.userRoles?.includes('BL_UserAdmin')) {
      return (
        <LoadingButton
          type="button"
          startIcon={<ManageAccounts />}
          loadingPosition="start"
          color="primary"
          variant="contained"
          onClick={e => {
            navigateToManageUsers();
          }}
        >
          Manage User
        </LoadingButton>
      );
    } else {
      return null;
    }
  }

  const [isSendingWelcomeEmail, setIsSendingWelcomeEmail] = useState<boolean>(false);
  const renderResetPasswordButton = useMemo(() => {
    if (!canResetPassword || !clientDetail?.shouldShowPasswordModificationButton) {
      return null;
    }

    if (
      clientDetail?.shouldShowPasswordModificationButton &&
      clientDetail?.canRegenerateWelcomeEmail
    ) {
      return (
        <LoadingButton
          type="button"
          // eslint-disable-next-line react/jsx-no-undef
          startIcon={<EmailOutlined />}
          disabled={isSendingWelcomeEmail}
          loading={isSendingWelcomeEmail}
          loadingPosition="start"
          color="primary"
          variant="contained"
          onClick={async () => {
            try {
              setIsSendingWelcomeEmail(true);
              await sendClientWelcomeEmail(clientDetail?.personId);
              enqueueSnackbar('Email Generated', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? `Error sending welcome email, please try again.`, {
                variant: 'error'
              });
            } finally {
              setIsSendingWelcomeEmail(false);
            }
          }}
        >
          Regenerate Welcome Email
        </LoadingButton>
      );
    }

    if (
      clientDetail?.shouldShowPasswordModificationButton &&
      !clientDetail?.canRegenerateWelcomeEmail
    ) {
      return (
        <LoadingButton
          type="button"
          startIcon={<Restore />}
          disabled={isResettingUser}
          loading={isResettingUser}
          loadingPosition="start"
          color="primary"
          variant="contained"
          onClick={async () => {
            const result = window.confirm(`Are you sure you want to reset this password?`);
            if (result) {
              try {
                setIsResettingUser(true);
                await resetClientPassword(clientDetail?.personId);
                enqueueSnackbar('Email Generated', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? `Error resetting user password, please try again.`,
                  {
                    variant: 'error'
                  }
                );
              } finally {
                setIsResettingUser(false);
              }
            }
          }}
        >
          Reset Password
        </LoadingButton>
      );
    }

    // Base case: no buttons to render
    return null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canResetPassword,
    isResettingUser,
    isSendingWelcomeEmail,
    clientDetail?.personId,
    clientDetail?.canRegenerateWelcomeEmail,
    clientDetail?.shouldShowPasswordModificationButton,
    isSendingWelcomeEmail
  ]);

  // Set Selected Tab based on page URL
  // not currently used at the moment, but setup in case we want to switch the tabs to links
  useEffect(() => {
    if (window.location.href.includes('contact-info')) {
      setSelectedTab(clientDetailTabs.contactInformation);
    }
    if (window.location.href.includes('builds')) {
      setSelectedTab(clientDetailTabs.builds);
    }
    if (window.location.href.includes('additional-contacts')) {
      setSelectedTab(clientDetailTabs.additionalContacts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  useEffect(() => {
    fetchClientDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  const agentId = useMemo(() => {
    return clientDetail?.contactInformation?.agentId
      ? clientDetail.contactInformation.agentId
      : // the logged in user needs to have the internal agent role and have a personId to set a default
      user?.userRoles?.includes(UserRoles.BLAgent) && user?.externalUser?.personId
      ? user.externalUser.personId
      : '';
  }, [user, clientDetail]);

  return (
    <Page pageHasStickyHeaders title={title}>
      <PageTitleWithButton
        marginBottom="0"
        pageTitleBackProps={{
          title: title,
          backText: 'Back to purchasers',
          backUrl: '/sales/contracting/purchasers'
        }}
        button={
          !!clientDetail ? (
            <ButtonGroup>
              {renderResetPasswordButton}
              {linkPerson()}
            </ButtonGroup>
          ) : null
        }
        additionalHeaderContent={
          <Grid
            container
            justifyContent={{ xs: 'center', sm: 'flex-end' }}
            item
            xs={12}
            sx={{
              mb: { xs: 0.75, sm: 0.5 },
              mt: { xs: 1, sm: 0 }
            }}
          >
            {isSalesAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    id="hide-client-builds"
                    checked={isBuildHidden}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setIsBuildHidden(event.target.checked)
                    }
                  />
                }
                label="Hide Builds"
              />
            )}
            {isCloser && (
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    id="hide-purchaser"
                    checked={isPurchaserHidden}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setIsPurchaserHidden(event.target.checked)
                    }
                  />
                }
                label="Hide from Purchaser list"
              />
            )}
          </Grid>
        }
      />
      {isLoadingClientDetail && <Loader type="fullscreen" position="centered" />}
      {(clientDetail || personId === 'add') && (
        <Formik
          initialValues={{
            isActive: clientDetail?.isActive ?? true,
            contactInformation: {
              agentId,
              firstName: clientDetail?.contactInformation?.firstName ?? '',
              lastName: clientDetail?.contactInformation?.lastName ?? '',
              contractName: clientDetail?.contactInformation?.contractName ?? '',
              address: clientDetail?.contactInformation?.address ?? '',
              address2: clientDetail?.contactInformation?.address2 ?? '',
              city: clientDetail?.contactInformation?.city ?? '',
              state: clientDetail?.contactInformation?.state ?? '',
              zip: clientDetail?.contactInformation?.zip ?? '',
              email: clientDetail?.contactInformation?.email ?? '',
              phoneNumber: clientDetail?.contactInformation?.phoneNumber ?? '',
              buyerHasAgent: clientDetail?.contactInformation?.buyerHasAgent ?? false,
              agentFirstName: clientDetail?.contactInformation?.agentFirstName ?? '',
              agentLastName: clientDetail?.contactInformation.agentLastName ?? '',
              agentEmail: clientDetail?.contactInformation.agentEmail ?? '',
              agentPhone: clientDetail?.contactInformation.agentPhone ?? '',
              agentSellingCompany: clientDetail?.contactInformation?.agentSellingCompany ?? ''
            },
            additionalContacts: clientDetail?.additionalContacts ?? []
          }}
          enableReinitialize={true}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={clientDetailSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IClientDetail = {
                ...values,
                hideBuilds: isBuildHidden,
                hidePurchaser: isPurchaserHidden,
                contactInformation: {
                  ...values.contactInformation,
                  agentId: values.contactInformation.agentId
                    ? Number(values.contactInformation.agentId)
                    : null
                },
                canRegenerateWelcomeEmail: clientDetail?.canRegenerateWelcomeEmail ?? false,
                shouldShowPasswordModificationButton:
                  clientDetail?.shouldShowPasswordModificationButton ?? false
              };

              if (personId === 'add') {
                const newPersonId = await createClient(data);
                actions.resetForm();
                history.push(`/sales/contracting/purchasers/${newPersonId}`);
              } else {
                await updateClient(personId, data);
                await fetchClientDetail();
              }

              actions.resetForm();
              enqueueSnackbar(
                `Client has been ${personId === 'add' ? 'created' : 'updated'} successfully!`,
                { variant: 'success' }
              );
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Purchaser was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            handleSubmit,
            validateForm,
            validateField,
            setFieldTouched,
            errors,
            touched
          }) => {
            const additionalContactsErrorsAreAllNull =
              errors?.additionalContacts?.length > 0 &&
              (errors?.additionalContacts as any[])?.every(error => !error);
            const onlyAdditionalContactsErrors =
              Object.keys(errors).length === 1 && Array.isArray(errors?.additionalContacts);

            return (
              <Form onSubmit={handleSubmit} autoComplete="none">
                {isSubmitting && <Loader type="fullscreen" position="centered" />}

                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />

                {Object.keys(errors).length !== 0 && (
                  <AddEditCustomErrorDisplay
                    clientDetailTabLinks={clientDetailTabLinks}
                    // We need to clone the errors object to prevent Formik from mutating it and causing a re-render
                    // We want all errors to display when submitted, and not disappear until the user submits again
                    errors={errors}
                    touched={touched}
                    additionalContactsErrorsAreAllNull={additionalContactsErrorsAreAllNull}
                    onlyAdditionalContactsErrors={onlyAdditionalContactsErrors}
                  />
                )}

                <AddEditClientTabs
                  errors={errors}
                  additionalContactsErrorsAreAllNull={additionalContactsErrorsAreAllNull}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                  clientDetailTabLinks={clientDetailTabLinks}
                  setFieldTouched={setFieldTouched}
                  values={values}
                  validateForm={validateForm}
                  validateField={validateField}
                />

                {selectedTab === clientDetailTabs.contactInformation && (
                  <ContactInfoTab
                    userId={user?.externalUser?.personId}
                    clientDetail={clientDetail}
                    disableAgentField={user?.userRoles?.includes(UserRoles.BLAgent)}
                  />
                )}

                {selectedTab === clientDetailTabs.additionalContacts && (
                  <AdditionalContactsTab
                    clientDetail={clientDetail}
                    fetchClientDetail={fetchClientDetail}
                    manageUser={contact => navigateToManageUsersAdditionalContact(contact)}
                    canManageUsers={
                      user?.userRoles?.includes('BL_Admin') ||
                      user?.userRoles?.includes('BL_UserAdmin')
                    }
                    agentId={agentId}
                    contractName={clientDetail?.contactInformation?.contractName ?? ''}
                    validateForm={validateForm}
                  />
                )}
                {selectedTab === clientDetailTabs.builds && <BuildsTab personId={personId} />}
                <Box mt={2}>
                  <Divider />
                  <Box
                    display="flex"
                    alignItems="center"
                    flexDirection={{
                      xs: 'column',
                      sm: 'row'
                    }}
                    justifyContent={{
                      xs: 'center',
                      sm: 'space-between'
                    }}
                    mt={1}
                  >
                    <ButtonGroup>
                      <Button
                        // We can't use dirty here because if an additional contact row is removed, so are those
                        // 'dirty' properties, but the form is still dirty. If we have it, it may block the user
                        // from saving the form.
                        disabled={isSubmitting}
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                          await validateForm();
                          handleSubmit();
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        disabled={isSubmitting}
                        startIcon={<Close />}
                        onClick={() => {
                          history.push('/sales/contracting/purchasers');
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
    </Page>
  );
};
